import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AdminDataService,
  OrchestratorAdminJobItem,
  OrchestratorAdminJobUpdateRequest,
} from '../../controller/admin-data.service';

type OrchestratorJobState = 'ALL' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'ERROR' | 'CANCELLED';

@Component({
  selector: 'app-orchestrator-db-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './orchestrator-db-management.component.html',
  styleUrls: ['./orchestrator-db-management.component.scss']
})
export class OrchestratorDbManagementComponent implements OnInit {
  public readonly stateOptions: OrchestratorJobState[] = ['ALL', 'PENDING', 'PROCESSING', 'COMPLETED', 'ERROR', 'CANCELLED'];

  public jobs: OrchestratorAdminJobItem[] = [];
  public stateFilter: OrchestratorJobState = 'ALL';
  public searchQuery: string = '';
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public feedbackMessage: string = '';

  public activeWorkers: number = 0;
  public processingJobs: number = 0;
  public pendingJobs: number = 0;
  public generatedAt: string = '';

  public editingVideoId: string | null = null;
  public editDraft: {
    state: OrchestratorAdminJobItem['state'];
    priority: number;
    reprocess: boolean;
    machine_id: string;
    preferred_machine: string;
    prefer_deadline: string;
    s3_path: string;
  } | null = null;

  constructor(private adminDataService: AdminDataService) {}

  ngOnInit(): void {
    this.loadJobs();
  }

  public loadJobs(): void {
    this.feedbackMessage = '';
    this.isLoading = true;

    const normalizedState = this.stateFilter === 'ALL' ? null : this.stateFilter;
    this.adminDataService.getOrchestratorJobs(normalizedState, this.searchQuery.trim(), 250).subscribe({
      next: (response) => {
        this.jobs = response.items;
        this.activeWorkers = response.active_workers;
        this.processingJobs = response.processing_jobs;
        this.pendingJobs = response.pending_jobs;
        this.generatedAt = response.generated_at;
        this.isLoading = false;
      },
      error: () => {
        this.feedbackMessage = 'No se pudieron cargar los jobs del orquestador.';
        this.jobs = [];
        this.isLoading = false;
      }
    });
  }

  public startEdit(job: OrchestratorAdminJobItem): void {
    this.editingVideoId = job.video_id;
    this.editDraft = {
      state: job.state,
      priority: Number(job.priority),
      reprocess: Boolean(job.reprocess),
      machine_id: job.machine_id ?? '',
      preferred_machine: job.preferred_machine ?? '',
      prefer_deadline: job.prefer_deadline ?? '',
      s3_path: job.s3_path ?? '',
    };
  }

  public cancelEdit(): void {
    this.editingVideoId = null;
    this.editDraft = null;
  }

  public saveEdit(job: OrchestratorAdminJobItem): void {
    if (!this.editDraft || this.editingVideoId !== job.video_id) {
      return;
    }

    const payload: OrchestratorAdminJobUpdateRequest = {};

    if (this.editDraft.state !== job.state) {
      payload.state = this.editDraft.state as OrchestratorAdminJobUpdateRequest['state'];
    }

    const normalizedPriority = Number(this.editDraft.priority);
    if (Number.isFinite(normalizedPriority) && normalizedPriority !== Number(job.priority)) {
      payload.priority = Math.max(0, Math.min(1000, Math.floor(normalizedPriority)));
    }

    if (this.editDraft.reprocess !== Boolean(job.reprocess)) {
      payload.reprocess = this.editDraft.reprocess;
    }

    const machineId = this.normalizeNullable(this.editDraft.machine_id);
    if ((job.machine_id ?? null) !== machineId) {
      payload.machine_id = machineId;
    }

    const preferredMachine = this.normalizeNullable(this.editDraft.preferred_machine);
    if ((job.preferred_machine ?? null) !== preferredMachine) {
      payload.preferred_machine = preferredMachine;
    }

    const preferDeadline = this.normalizeNullable(this.editDraft.prefer_deadline);
    if ((job.prefer_deadline ?? null) !== preferDeadline) {
      payload.prefer_deadline = preferDeadline;
    }

    const s3Path = (this.editDraft.s3_path || '').trim();
    if (s3Path && s3Path !== (job.s3_path || '')) {
      payload.s3_path = s3Path;
    }

    if (Object.keys(payload).length === 0) {
      this.feedbackMessage = 'No hay cambios para guardar.';
      return;
    }

    this.feedbackMessage = '';
    this.isSaving = true;
    this.adminDataService.updateOrchestratorJob(job.video_id, payload).subscribe({
      next: (updated) => {
        this.jobs = this.jobs.map((item) => (item.video_id === updated.video_id ? updated : item));
        this.feedbackMessage = `Job ${updated.video_id} actualizado.`;
        this.isSaving = false;
        this.cancelEdit();
      },
      error: () => {
        this.feedbackMessage = `No se pudo actualizar el job ${job.video_id}.`;
        this.isSaving = false;
      }
    });
  }

  public isEditing(job: OrchestratorAdminJobItem): boolean {
    return this.editingVideoId === job.video_id;
  }

  private normalizeNullable(value: string): string | null {
    const normalized = (value || '').trim();
    return normalized.length > 0 ? normalized : null;
  }
}

